/**
 * @file archiveOrdersLeft.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Archiving purchase orders on their own when they are
 *              everything bought for a customer order (Cam, 3 Nov 2026 —
 *              SO-D10696: "When user archives an order from transport planning,
 *              the PO is archived successfully, but the SO seems to get left
 *              behind"). Archive PO… took the PO off the order and left the
 *              order live with nothing bought for it, and its history said
 *              nothing.
 *
 *              Before the checklist opens, POST /api/purchase-orders/archive-check
 *              names those orders (services/poArchiveService.ordersLeftWithoutPo)
 *              and the person chooses for each — no default:
 *                'archive' — the order goes too, with all its live POs (a PO-H
 *                            included), through the order's own archive
 *                            (PATCH /customer-orders/:id/archive);
 *                'keep'    — the order stays, to be bought elsewhere; the PO
 *                            goes alone and the order's history says so.
 *
 *              Pure: what the question says, when it is answered, which POs the
 *              checklist lists, how the archive is split into calls, and the
 *              words afterwards. The components are ArchiveOrdersLeftDialog and
 *              ArchivePurchaseOrdersFlow.
 */

import { formatDate } from './dateFormatters';

const list = (xs) => {
  const a = (xs || []).filter(Boolean);
  if (a.length <= 1) return a.join('');
  return `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`;
};

/** "PO-D10665 is everything bought for SO-D10696 (… , delivering 24/10/2026)." */
export function orderLeftText(order) {
  const pos = list(order?.po_numbers || []);
  const many = (order?.po_numbers || []).length > 1;
  const who = [order?.account_no, order?.customer].filter(Boolean).join(' ');
  const when = order?.delivery_date ? `, delivering ${formatDate(order.delivery_date)}` : '';
  return `${pos || 'This purchase order'} ${many ? 'are' : 'is'} everything bought for ${order?.order_number || 'its customer order'}`
    + `${who ? ` (${who}${when})` : when ? ` (${when.slice(2)})` : ''}. `
    + `Archiving ${many ? 'them' : 'it'} on ${many ? 'their' : 'its'} own leaves the order live with nothing bought for it.`;
}

/** The two answers, in words. */
export const CHOICES = [
  { value: 'archive', label: 'Archive the order too', hint: 'The customer order goes with its purchase orders — it is off the planning boards and Search Customer Orders can restore it.' },
  { value: 'keep', label: 'Keep the order', hint: 'Only the purchase order goes. The customer order stays, to be bought elsewhere — raise a new PO for it.' }
];

/** Every order answered? { ok, reason } — the reason says which is not. */
export function choicesComplete(orders, choices = {}) {
  const open = (orders || []).filter(o => !choices[o.id]);
  if (!open.length) return { ok: true, reason: null };
  return { ok: false, reason: `Choose what happens to ${list(open.map(o => o.order_number))} first` };
}

/**
 * The checklist's POs: the ones chosen, then — for each order going too —
 * its other live POs (its PO-H, another PO-D), so a supplier or haulier who
 * has one can be told before anything is archived.
 */
export function checklistPos(pos, orders, choices = {}) {
  const out = [...(pos || [])];
  const seen = new Set(out.map(p => Number(p.id)));
  for (const o of orders || []) {
    if (choices[o.id] !== 'archive') continue;
    for (const p of o.pos || []) {
      if (seen.has(Number(p.id))) continue;
      seen.add(Number(p.id));
      out.push({ ...p, for_order: o.order_number });
    }
  }
  return out;
}

/**
 * How the ticked POs are archived:
 *   orderCalls — each order going too, with its ticked POs (each PO once: the
 *                first order claiming a shared one takes it). An order none of
 *                whose chosen POs is ticked any more is not archived — it is
 *                listed in `notArchived` (its PO stays, so it is not left bare).
 *   rest       — every other ticked PO, archived on its own.
 *   kept       — the orders the person chose to keep.
 */
export function planArchive(tickedIds, orders, choices = {}) {
  const ticked = new Set((tickedIds || []).map(Number));
  const claimed = new Set();
  const orderCalls = [];
  const notArchived = [];
  for (const o of orders || []) {
    if (choices[o.id] !== 'archive') continue;
    const own = (o.po_ids || []).map(Number);
    if (!own.some(id => ticked.has(id))) { notArchived.push(o); continue; }
    const poIds = (o.pos || []).map(p => Number(p.id)).filter(id => ticked.has(id) && !claimed.has(id));
    poIds.forEach(id => claimed.add(id));
    orderCalls.push({ order: o, poIds });
  }
  const rest = [...ticked].filter(id => !claimed.has(id));
  const kept = (orders || []).filter(o => choices[o.id] === 'keep');
  return { orderCalls, rest, kept, notArchived };
}

/** The checklist's intro line about the orders going too (or '' when none). */
export function ordersGoingText(orders, choices = {}) {
  const going = (orders || []).filter(o => choices[o.id] === 'archive').map(o => o.order_number);
  if (!going.length) return '';
  return `${list(going)} ${going.length === 1 ? 'is' : 'are'} archived with ${going.length === 1 ? 'its' : 'their'} purchase orders.`;
}

/**
 * What happened to the orders, for the message afterwards:
 *   orders_archived — ['SO-D10696']
 *   orders_kept     — [{ order_number }] kept with nothing bought for them
 */
export function ordersOutcomeText(data) {
  const parts = [];
  const archived = data?.orders_archived || [];
  const kept = data?.orders_kept || [];
  if (archived.length) parts.push(`Archived with them: ${list(archived)} — nothing else was bought for ${archived.length === 1 ? 'it' : 'them'}; Search Customer Orders can restore ${archived.length === 1 ? 'it' : 'them'}.`);
  if (kept.length) {
    const names = list(kept.map(o => o.order_number));
    parts.push(`${names} ${kept.length === 1 ? 'was' : 'were'} kept and now ${kept.length === 1 ? 'has' : 'have'} nothing bought — raise a new PO, or archive ${kept.length === 1 ? 'it' : 'them'} on Search Customer Orders.`);
  }
  return parts.join(' ');
}
